import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useIntl } from "react-intl";

import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as CopyToClipboardIcon } from "assets/images/iconsV2/copy.svg";
import { ReactComponent as SuccessIcon } from "assets/images/iconsV2/check-circle-green.svg";
import testIds from "constants/testIds";
import BookmarkTab from "../NavIcons/BookmarkTab";

const INACTIVE_STATUSES = ["Canceled", "Archived", "Auto-closed"];

interface RelatedShipment {
  relation: string;
  shipment_id: string;
}

interface ShipmentIdentifierProps {
  isDataLoading: boolean;
  shipmentReference: string;
  isQuickRequest: boolean;
}

function ShipmentIdentifier({ isDataLoading, shipmentReference, isQuickRequest }: ShipmentIdentifierProps) {
  const intl = useIntl();
  const [isCopyHovered, setIsCopyHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(shipmentReference).then(() => {
      // If successful, update the isCopied state value
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    });
  };

  const { data: shipmentData } = useGetShipmentBasic(shipmentReference, {
    enabled: !isQuickRequest && !!shipmentReference,
    refetchOnMount: false,
  });

  const parentShipment = shipmentData?.related_shipments?.find(
    (shipment: RelatedShipment) => shipment.relation === "Parent",
  );
  const status = shipmentData?.organization?.request_status;
  const showBookmarkButton = !isQuickRequest && !shipmentData?.shipment?.archived && !INACTIVE_STATUSES.includes(status);

  if (isDataLoading) {
    return (
      <div>
        <span className="skeleton topnav-v2__title-skeleton" data-testid={testIds.TOPNAV_V2_TITLE_SKELETON} />
      </div>
    );
  }

  const parentLabel = intl.formatMessage({ id: "general.parent", defaultMessage: "Parent" });

  return (
    <div className="d-flex align-items-center">
      <div className="d-flex align-items-center gap-xs">
        <div
          className="topnav-v2__shipment-copy-wrapper"
          onMouseEnter={() => setIsCopyHovered(true)}
          onMouseLeave={() => setIsCopyHovered(false)}
          onClick={handleCopy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleCopy()}
        >
          <span className="topnav-v2__shipment-id" data-testid={testIds.TOPNAV_V2_BADGE_SHIPMENT_REFERENCE_VALUE}>
            {shipmentReference}
          </span>
          {isCopyHovered && (
            <div className="topnav-v2__copy-icon-container">
              <OverlayTrigger placement="top" overlay={<Tooltip id="">{isCopied ? "Copied!" : "Click to copy"}</Tooltip>}>
                {isCopied ? <SuccessIcon className="success-icon" /> : <CopyToClipboardIcon className="copy-icon" />}
              </OverlayTrigger>
            </div>
          )}
        </div>
        {parentShipment && (
          <span className="topnav-v2__parent-shipment" data-testid={testIds.TOPNAV_V2_BADGE_PARENT_SHIPMENT_CONTAINER}>
            ({parentLabel}:{" "}
            <Link
              to={`/shipment/${parentShipment.shipment_id}`}
              className="topnav-v2__parent-link"
              data-testid={testIds.TOPNAV_V2_BADGE_PARENT_SHIPMENT_LINK}
            >
              {parentShipment.shipment_id}
            </Link>
            )
          </span>
        )}
      </div>

      {showBookmarkButton && <BookmarkTab shipmentId={shipmentReference} />}
    </div>
  );
}

export default ShipmentIdentifier;
